import React, { useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Checkbox from '@/components/General/inputs/Checkbox';
import DropdownMenu from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import useTableSelectionStore from '@/data/store/useTableSelectionStore';

const getNestedValue = (obj, path) => {
    if (!path) return '';
    return path.split('.').reduce((value, key) => value?.[key], obj) ?? '';
};

const renderValue = (column, item) => {
    const value = column.cardRenderer?.(item) ?? column.renderer?.(item) ?? getNestedValue(item, column.accessor);
    return typeof value === 'string' || typeof value === 'number'
        ? <Text style={styles.fieldValue}>{value}</Text>
        : value;
};

const TableCard = ({ tableId, item, itemId, columns, onPress, actions = [], batchSelection, onToggleSelect, cardHeaderRenderer, cardPreviewCount = 2, accentColor }) => {
    const [expanded, setExpanded] = useState(false);
    const isSelected = useTableSelectionStore(s => batchSelection ? s.isSelected(tableId, itemId) : false);
    const detailColumns = useMemo(() => columns.filter(column => !column.cardPrimary), [columns]);
    const visibleColumns = expanded ? detailColumns : detailColumns.slice(0, cardPreviewCount);
    const availableActions = useMemo(() => actions.filter(action => !action.isAvailable || action.isAvailable(item)), [actions, item]);

    return (
        <View style={styles.wrapper}>
            <View style={styles.card(isSelected, accentColor)} testID={`TableCard-${tableId}`}>
                <View style={styles.headerRow}>
                    {batchSelection && (
                        <Checkbox
                            testID={`TableRowCheckbox-${tableId}-${itemId}`}
                            checked={isSelected}
                            onChange={() => onToggleSelect?.(item)}
                            size={18}
                        />
                    )}
                    <Pressable testID={`TableCardHeader-${tableId}`} disabled={!onPress} onPress={onPress} style={styles.headerContent}>
                        {cardHeaderRenderer ? cardHeaderRenderer(item) : renderValue(columns[0], item)}
                    </Pressable>
                    {availableActions.length > 0 && (
                        <DropdownMenu options={availableActions.map(action => ({ ...action, onSelect: () => action.onSelect(item) }))} />
                    )}
                    {detailColumns.length > cardPreviewCount && (
                        <Pressable
                            testID={`TableCardExpand-${tableId}-${itemId}`}
                            accessibilityLabel={expanded ? 'Hide details' : 'View all columns'}
                            onPress={() => setExpanded(value => !value)}
                            style={styles.expandButton}
                        >
                            <Unicon name={expanded ? 'chevron-up' : 'chevron-down'} size={20} colorVariant="textSecondary" />
                        </Pressable>
                    )}
                </View>

                {visibleColumns.length > 0 && (
                    <View style={styles.fields(expanded)}>
                        {visibleColumns.map(column => (
                            <View key={column.id || column.accessor || column.value} style={styles.field}>
                                <Text style={styles.label}>{column.value}</Text>
                                <View style={styles.value}>{renderValue(column, item)}</View>
                            </View>
                        ))}
                    </View>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create((theme, rt) => ({
    wrapper: {
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: 5,
    },
    card: (selected, accentColor) => ({
        backgroundColor: selected ? theme.colors.primaryContainer : (rt.themeName === 'dark' ? theme.colors.surface : theme.colors.surfaceElevated),
        borderRadius: 3,
        borderLeftWidth: 3,
        borderLeftColor: accentColor || theme.colors.primary,
        padding: theme.spacing.md,
        gap: theme.spacing.md,
        boxShadow: theme.shadows.none,
        borderCurve: 'continuous',
        _web: {
            transition: 'background-color 0.15s ease, transform 0.15s ease',
            _hover: {
                transform: [{ translateY: -1 }],
            },
        },
    }),
    headerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    headerContent: {
        flex: 1,
        minWidth: 0,
        borderRadius: 3,
    },
    expandButton: {
        width: 34,
        height: 34,
        borderRadius: 3,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.surfaceVariant,
    },
    fields: (expanded) => ({
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing.md,
        paddingTop: expanded ? theme.spacing.sm : 0,
        borderTopWidth: expanded ? 1 : 0,
        borderTopColor: theme.colors.borderSubtle,
    }),
    field: {
        minWidth: 130,
        flex: 1,
        gap: 3,
    },
    label: {
        color: theme.colors.textTertiary,
        fontFamily: theme.fonts.bold,
        fontSize: theme.typography.micro,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    value: {
        minHeight: 20,
        justifyContent: 'center',
    },
    fieldValue: {
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
        fontSize: theme.typography.caption,
    },
}));

export default React.memo(TableCard);
